import { Socket } from "phoenix";
import "./main.css";
import { Elm } from "./Main.elm";
import copyToClipboard from "./copyToClipboard";

// TODO: remove this after it's been deployed for a while
navigator.serviceWorker.getRegistrations().then((registrations) => {
  for (let registration of registrations) {
    registration.unregister();
  }
});

const debugBots =
  process.env.ELM_APP_DEBUG_BOTS_ENABLED === "true" ||
  new URLSearchParams(window.location.search).has("bots");

const playerIdKey = "arts-as-one:playerId";
const playerNameKey = "playerName";

const envApiBaseUrl = process.env.ELM_APP_API_BASE_URL;

const serverBaseUrl = envApiBaseUrl || "arts-as-one-api.fly.dev";

const isSecure = window.location.protocol === "https:";
const socketProtocol = isSecure ? "wss" : "ws";
const protocol = isSecure ? "https" : "http";

const socket = new Socket(`${socketProtocol}://${serverBaseUrl}/socket`, {
  reconnectAfterMs: (tries) => Math.min(tries * 1000, 10000),
});
socket.connect();

let gameChannel;

const app = Elm.Main.init({
  node: document.getElementById("root"),
  flags: {
    apiBaseUrl: `${protocol}://${serverBaseUrl}/api`,
    basePath: process.env.PUBLIC_URL || "",
    maybePlayerId: debugBots ? null : localStorage.getItem(playerIdKey),
    maybeName: debugBots ? null : localStorage.getItem(playerNameKey),
    debugBots,
  },
});

app.ports.savePlayer.subscribe(({ id, name }) => {
  localStorage.setItem(playerIdKey, id);
  localStorage.setItem(playerNameKey, name);
});

const attachGameChannelListeners = (gc) => {
  gc.on("players:updated", ({ players }) => {
    app.ports.playersUpdated.send(players);
  });

  gc.on("game:updated", (gameState) => {
    app.ports.gameUpdated.send(gameState);
  });

  gc.on("round_ended", () => {
    app.ports.roundEnded.send();
  });

  gc.on("seconds_remaining:updated", ({ seconds_remaining }) => {
    app.ports.secondsRemainingUpdated.send(seconds_remaining);
  });

  gc.on("game:changed", ({ game }) => {
    app.ports.gameChanged.send(game);
  });
};

app.ports.copyUrl.subscribe(() => {
  const url = window.location.href;
  if (navigator.share) {
    navigator.share({ url });
  } else {
    copyToClipboard(url);
  }
});

app.ports.triggerSparkles.subscribe(() => {
  const dpr = window.devicePixelRatio || 1;
  const W = window.innerWidth;
  const H = window.innerHeight;

  const canvas = document.createElement("canvas");
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  Object.assign(canvas.style, {
    position: "fixed",
    top: "0",
    left: "0",
    width: W + "px",
    height: H + "px",
    pointerEvents: "none",
    zIndex: "9999",
  });
  document.body.appendChild(canvas);

  const ctx = canvas.getContext("2d");
  ctx.scale(dpr, dpr);

  const el = document.querySelector(".summary-section.drawing-complete");
  let cx = W / 2,
    cy = H / 2,
    sw = W * 0.5,
    sh = H * 0.5;
  if (el) {
    const r = el.getBoundingClientRect();
    cx = r.left + r.width / 2;
    cy = r.top + r.height / 2;
    sw = r.width;
    sh = r.height;
  }

  const COLORS = [
    "#FFD700",
    "#FF6B9D",
    "#4FC3F7",
    "#AED581",
    "#FFB74D",
    "#CE93D8",
    "#ffffff",
    "#FF8A65",
  ];
  const particles = Array.from({ length: 72 }, () => {
    const x = cx + (Math.random() - 0.5) * sw;
    const y = cy + (Math.random() - 0.5) * sh;
    const angle = Math.random() * Math.PI * 2;
    const speed = Math.random() * 3.5 + 0.8;
    return {
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 2.2,
      size: Math.random() * 5 + 2.5,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      alpha: 1,
      rot: Math.random() * Math.PI * 2,
      rotV: (Math.random() - 0.5) * 0.18,
      type: Math.floor(Math.random() * 3),
    };
  });

  const DURATION = 2000;
  let t0 = null;

  function drawStar(ctx, s) {
    ctx.beginPath();
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const r = i % 2 === 0 ? s : s * 0.4;
      i === 0
        ? ctx.moveTo(Math.cos(a) * r, Math.sin(a) * r)
        : ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    }
    ctx.closePath();
    ctx.fill();
  }

  function step(ts) {
    if (!t0) t0 = ts;
    const progress = Math.min((ts - t0) / DURATION, 1);
    ctx.clearRect(0, 0, W, H);

    for (const p of particles) {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.08;
      p.rot += p.rotV;
      p.alpha = Math.max(0, 1 - progress * 1.35);

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.globalAlpha = p.alpha;
      ctx.fillStyle = p.color;

      const s = p.size;
      if (p.type === 0) {
        drawStar(ctx, s);
      } else if (p.type === 1) {
        ctx.beginPath();
        ctx.arc(0, 0, s * 0.6, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-s * 0.3, -s, s * 0.6, s * 2);
      }

      ctx.restore();
    }

    if (progress < 1) {
      requestAnimationFrame(step);
    } else {
      canvas.remove();
    }
  }

  requestAnimationFrame(step);
});

app.ports.joinGame.subscribe(([gameId, playerId]) => {
  if (gameChannel) {
    gameChannel.leave();
  }

  gameChannel = socket.channel(`game:${gameId}`, { player_id: playerId });

  attachGameChannelListeners(gameChannel);

  gameChannel
    .join()
    .receive("ok", (response) => {
      app.ports.gameUpdated.send(response);
      if (debugBots) {
        gameChannel.push("setup_bots", {});
      }
    })
    .receive("error", () => {
      // Game no longer exists (server restarted); return to home screen.
      window.location.href = "/";
    });
});

app.ports.publishDrawing.subscribe((drawing) => {
  gameChannel.push("publish_drawing", drawing);
});

app.ports.startGame.subscribe((data) => {
  gameChannel.push("start_game", data);
});

app.ports.changeGame.subscribe((data) => {
  gameChannel.push("change_game", data);
});

window.onbeforeunload = () => {
  if (gameChannel) {
    gameChannel.leave();
  }
};
